import React, { useEffect, useRef, useState } from "react";
import { useDownloadAttachment } from "@/shared/api/attachments";
import {
  Check, Copy, Globe, Download,
  ThumbsUp, ThumbsDown, RotateCcw, Maximize2,
  Play, Pause, AlertTriangle
} from "lucide-react";
import { FileTypeIcon, fileType } from "@/shared/components/icons/filetype";
import { ViewerDialog } from "@/shared/components/viewer";
import { Chip } from "@/shared/components/ui-kit";
import { confirmDangerCls, confirmDialogCls } from "@/shared/components/settings-atoms";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import type {
  Artifact, Attachment, ChartPoint, Message, MessageBlock, MessageError, Vote,
} from "@/shared/types";
import CodeBlock from "@/shared/components/markdown/CodeBlock";
import Markdown from "@/shared/components/markdown/Markdown";

import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";

/* ---------- image block (agent-generated or referenced image) ---------- */
export function ImageBlock({ src, alt, caption }: { src: string; alt?: string; caption?: string }) {
  return (
    <figure className="m-0 min-w-0">
      <img
        src={src}
        alt={alt || caption || ""}
        className="rounded-[12px] border border-[var(--hairline)] max-w-full max-h-[340px] object-cover shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
      />
      {caption && (
        <figcaption className="mt-1.5 text-[11px] text-[var(--text-tertiary)] tracking-[-0.005em]">{caption}</figcaption>
      )}
    </figure>
  );
}

/* ---------- uploaded-file chips & thumbnails ---------- */
/** A file in the chat. One the server holds (`id`) downloads on click; one still in the
    browser opens in the viewer. */
export function FileChip({ file }: { file: Attachment }) {
  const [open, setOpen] = useState(false);
  const download = useDownloadAttachment();
  const onServer = !!file.id && !file.file;
  return <>
    <button
      onClick={() => (onServer ? download.mutate({ id: file.id!, name: file.name }) : setOpen(true))}
      className="press group flex items-center gap-2.5 rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] px-2.5 py-2 hover:bg-[var(--surface-sunk)] min-w-0 max-w-full text-left"
      aria-label={`${onServer ? "Download" : "Preview"} ${file.name}`}
    >
      {file.kind === "image" && file.previewSrc ? <img src={file.previewSrc} alt="" className="w-9 h-9 shrink-0 rounded-[8px] object-cover" /> : <span className="shrink-0"><FileTypeIcon kind={file.kind} size={36} /></span>}
      <span className="min-w-0 pr-1">
        <span className="block text-[12px] font-medium text-[var(--text-primary)] truncate">{file.name}</span>
        <span className="block text-[10.5px] text-[var(--text-secondary)] truncate mt-1">{fileType(file.kind).label}{file.size ? ` · ${file.size}` : ""}</span>
      </span>
    </button>
    {open && <ViewerDialog mode="file" title={file.name} file={{ name: file.name, file: file.file, url: file.url || file.previewSrc }} onClose={() => setOpen(false)} />}
  </>;
}

export function AttachmentRow({ files, align = "end" }: { files: Attachment[]; align?: "start" | "end" }) {
  return <div className={`flex flex-wrap gap-2 min-w-0 ${align === "end" ? "justify-end" : ""}`}>
    {files.map((file, i) => <FileChip key={i} file={file} />)}
  </div>;
}

/* ---------- inline HTML render (small, lives in the transcript) ---------- */
export function InlineHtml({ title, height = 240, srcDoc }: { title?: string; height?: number; srcDoc: string }) {
  return (
    <div className="rounded-[12px] border border-[var(--hairline-strong)] bg-[var(--surface)] overflow-hidden min-w-0 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
      <div className="h-8 flex items-center gap-2 px-3 bg-[var(--surface-sunk)] border-b hairline">
        <Globe className="w-3.5 h-3.5 text-[var(--text-tertiary)] shrink-0" strokeWidth={1.75} />
        <span className="text-[11.5px] text-[var(--text-secondary)] truncate tracking-[-0.005em]">{title}</span>
        <div className="flex-1" />
        <span className="text-[9.5px] font-mono text-[var(--text-quaternary)] uppercase tracking-[0.06em] shrink-0">HTML</span>
      </div>
      <iframe
        title={title}
        sandbox=""
        srcDoc={srcDoc}
        className="w-full block bg-white"
        style={{ height }}
      />
    </div>
  );
}

/* ---------- artifact card (chat-anchored, opens the side viewer) ---------- */
export const ARTIFACT_META = {
  Document: { kind: "doc" },
  PDF:      { kind: "pdf" },
  Sheet:    { kind: "sheet" },
  HTML:     { kind: "html" },
  Slides:   { kind: "slides" },
};

export function ArtifactCard({ artifact, onOpen, active }: {
  artifact: Artifact;
  onOpen?: (a: Artifact) => void;
  active?: boolean;
}) {
  const meta = ARTIFACT_META[artifact.type] || ARTIFACT_META.Document;
  const versions = artifact.versions ?? [];
  return (
    <button
      onClick={() => onOpen?.(artifact)}
      className={`group press w-full text-left flex items-center gap-3 rounded-[12px] ring-1 bg-[var(--surface)] px-3 py-2.5 card-hover max-w-md min-w-0 ${
        active ? "ring-[var(--hairline-focus)]" : "ring-[var(--hairline)]"
      }`}
    >
      {/* @ts-expect-error TODO(types): FileTypeIcon declares no `radius` prop — dead prop, silently dropped (SMELLS.md #34); frozen under the behaviour freeze. */}
      <FileTypeIcon kind={meta.kind} size={38} radius={9} />
      <div className="min-w-0 flex-1">
        {/* The title wraps to a second line rather than losing its end beside the chip and "Open" on a phone */}
        <div className="text-[13px] font-medium text-[var(--text-primary)] tracking-[-0.005em] flex items-center gap-1.5 min-w-0">
          <span className="min-w-0 line-clamp-2 break-words" title={artifact.title}>{artifact.title}</span>
          {versions.length > 1 && (
            /* `!`: guarded by `versions.length > 1`, so there is a last one */
            <Chip tone={artifact.updated ? "warn" : "neutral"}>
              {artifact.updated ? `Updated · ${versions.at(-1)!.id}` : versions.at(-1)!.id}
            </Chip>
          )}
        </div>
        <div className="text-[10.5px] text-[var(--text-tertiary)] truncate mt-[1px]">
          {artifact.type}{artifact.pages ? ` · ${artifact.pages} page${artifact.pages > 1 ? "s" : ""}` : ""} · {artifact.age}
          {versions.length > 1 ? ` · ${versions.length} versions` : ""}
        </div>
      </div>
      <span className="shrink-0 text-[12px] text-[var(--text-tertiary)] group-hover:text-[var(--text-primary)] transition-colors flex items-center gap-1">
        {active ? "Viewing" : <>Open <Maximize2 className="w-3 h-3" strokeWidth={1.75} /></>}
      </span>
    </button>
  );
}

/* ---------- inline chart block ---------- */
const CHART_ACCENT = "#6d8df7";
const tooltipStyle = {
  background: "var(--surface)",
  border: "1px solid var(--hairline-strong)",
  borderRadius: 8,
  fontSize: 11,
  color: "var(--text-primary)",
  boxShadow: "0 8px 24px -12px rgba(0,0,0,0.15)",
};

/* Axis labels stay short at any magnitude — 1400000 reads 1.4M and fits the axis */
const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
const axisNumber = (v: number) => compact.format(v);

export function ChartBlock({ title, kind = "bar", data, note }: {
  title?: string;
  kind?: "bar" | "line";
  data: ChartPoint[];
  note?: string;
}) {
  const axis = { fontSize: 10, fill: "var(--text-tertiary)" };
  return (
    <div className="rounded-[12px] border border-[var(--hairline-strong)] bg-[var(--surface)] p-3.5 min-w-0 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
      {title && <div className="text-[12px] font-medium text-[var(--text-secondary)] tracking-[-0.005em] mb-2.5">{title}</div>}
      <div style={{ height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          {kind === "line" ? (
            <LineChart data={data} margin={{ top: 4, right: 8, left: -6, bottom: 0 }}>
              <CartesianGrid stroke="var(--hairline)" vertical={false} />
              <XAxis dataKey="label" tick={axis} axisLine={false} tickLine={false} />
              <YAxis tick={axis} axisLine={false} tickLine={false} width={44} tickFormatter={axisNumber} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: "var(--hairline-strong)" }} />
              <Line type="monotone" dataKey="value" stroke={CHART_ACCENT} strokeWidth={2} dot={false} />
            </LineChart>
          ) : (
            <BarChart data={data} margin={{ top: 4, right: 8, left: -6, bottom: 0 }}>
              <CartesianGrid stroke="var(--hairline)" vertical={false} />
              <XAxis dataKey="label" tick={axis} axisLine={false} tickLine={false} />
              <YAxis tick={axis} axisLine={false} tickLine={false} width={44} tickFormatter={axisNumber} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "var(--accent-hover)" }} />
              <Bar dataKey="value" fill={CHART_ACCENT} radius={[5, 5, 0, 0]} maxBarSize={34} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      {note && <div className="text-[10.5px] text-[var(--text-tertiary)] mt-2">{note}</div>}
    </div>
  );
}

/* ---------- audio / voice message ---------- */
const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export function AudioBlock({ duration = 12, label = "Voice message" }: { duration?: number; label?: string }) {
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  // `!` at every clearInterval: the ref starts null and clearInterval(null) is
  // a harmless no-op, but its signature does not admit null.
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => clearInterval(timer.current!), []);

  const toggle = () => {
    if (playing) {
      clearInterval(timer.current!);
      setPlaying(false);
      return;
    }
    setPlaying(true);
    timer.current = setInterval(() => {
      setT((prev) => {
        if (prev + 0.1 >= duration) {
          clearInterval(timer.current!);
          setPlaying(false);
          return 0;
        }
        return prev + 0.1;
      });
    }, 100);
  };

  const bars = Array.from({ length: 36 }, (_, i) => 5 + ((i * 37) % 17));
  const played = t / duration;

  return (
    <div className="inline-flex items-center gap-3 rounded-[22px] bg-[var(--surface)] border border-[var(--hairline-strong)] pl-2 pr-4 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.03)]" title={label}>
      <button
        onClick={toggle}
        className="press w-8 h-8 shrink-0 rounded-full bg-[var(--brand)] text-[var(--brand-foreground)] hover:bg-[var(--brand-hover)] flex items-center justify-center"
      >
        {playing ? <Pause className="w-3.5 h-3.5" strokeWidth={2} /> : <Play className="w-3.5 h-3.5 ml-[2px]" strokeWidth={2} />}
      </button>
      <div className="flex items-center gap-[2px] h-7">
        {bars.map((h, i) => (
          <span
            key={i}
            className="w-[2.5px] rounded-full transition-colors duration-100"
            style={{
              height: h,
              background: i / bars.length <= played && (playing || t > 0) ? "var(--text-primary)" : "var(--text-quaternary)",
            }}
          />
        ))}
      </div>
      <span className="font-mono text-[10.5px] text-[var(--text-tertiary)] tabular-nums shrink-0">
        {fmtTime(playing || t > 0 ? t : duration)}
      </span>
    </div>
  );
}

/* ---------- error state with retry ---------- */
export function ErrorBubble({ error, onRetry, retryLabel = "Retry" }: { error: MessageError; onRetry?: () => void; retryLabel?: string }) {
  return (
    <div className="rounded-[12px] border border-[var(--danger-ring)] bg-[var(--danger-bg)] px-3.5 py-3 max-w-md min-w-0">
      <div className="flex items-center gap-2 text-[13px] font-medium text-[var(--danger)] tracking-[-0.005em]">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
        {error.title}
      </div>
      <div className="text-[12px] text-[var(--text-secondary)] mt-1 leading-[1.55]">{error.detail}</div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="press mt-2.5 h-7 px-3 rounded-full bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] text-[12px] text-[var(--text-primary)] flex items-center gap-1.5 hover:bg-[var(--accent-hover)]"
        >
          <RotateCcw className="w-3 h-3" strokeWidth={1.75} />
          {retryLabel}
        </button>
      )}
    </div>
  );
}

/* ---------- a turn that was stopped on purpose ---------- */
/** Under the last message when the latest turn halted. Continue picks it back
    up where it stopped; sending a new message does the same with your words. */
export function RunStopped({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="flex items-center gap-2.5 pl-9 fade-in">
      <span className="text-[12px] text-[var(--text-tertiary)] tracking-[-0.005em]">Stopped</span>
      <button
        onClick={onContinue}
        className="press h-8 px-3.5 rounded-[8px] ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] text-[12px] text-[var(--text-primary)] hover:bg-[var(--surface-sunk)] flex items-center gap-1.5"
      >
        <Play className="w-3 h-3" strokeWidth={2} />
        Continue
      </button>
    </div>
  );
}

/* ---------- hover actions under agent messages ---------- */
export function MessageActions({ text, vote = null, onVote, onRegenerate }: {
  /** What Copy takes; a function when it is worth building only on a click (an answer with its sources). */
  text?: string | (() => string);
  vote?: Vote | null;
  /** Absent: nothing here is saved to rate — a turn in flight, or not its run's last — so no thumbs. */
  onVote?: (value: Vote | null) => void;
  /** The answer written again; what it would do again comes back to ask about first. */
  onRegenerate?: (repeatActions?: boolean) => Promise<string[] | null> | void;
}) {
  const [copied, setCopied] = useState(false);
  const [redo, setRedo] = useState<string[] | null>(null);
  const regenerate = async (repeatActions = false) => {
    setRedo(null);
    setRedo((await onRegenerate?.(repeatActions)) || null);
  };
  const copy = () => {
    navigator.clipboard?.writeText((typeof text === "function" ? text() : text) || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  const btn = "press w-6 h-6 rounded-[6px] flex items-center justify-center hover:bg-[var(--accent-hover)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]";
  return (
    // a vote stays in sight: it is what the reader said about this answer
    <div className={`flex items-center gap-0.5 mt-1.5 transition-opacity duration-150 ${vote ? "" : "opacity-0 group-hover/msg:opacity-100"}`}>
      <button className={btn} title="Copy" onClick={copy}>
        {copied ? <Check className="w-3 h-3 text-[var(--success)]" strokeWidth={2} /> : <Copy className="w-3 h-3" strokeWidth={1.75} />}
      </button>
      {onVote && (
        <>
          <button className={btn} title="Good response" aria-pressed={vote === "up"} onClick={() => onVote(vote === "up" ? null : "up")}>
            <ThumbsUp className={`w-3 h-3 ${vote === "up" ? "text-[var(--success)]" : ""}`} strokeWidth={1.75} fill={vote === "up" ? "currentColor" : "none"} />
          </button>
          <button className={btn} title="Bad response" aria-pressed={vote === "down"} onClick={() => onVote(vote === "down" ? null : "down")}>
            <ThumbsDown className={`w-3 h-3 ${vote === "down" ? "text-[var(--danger)]" : ""}`} strokeWidth={1.75} fill={vote === "down" ? "currentColor" : "none"} />
          </button>
        </>
      )}
      {onRegenerate && (
        <button className={btn} title="Regenerate" onClick={() => void regenerate()}>
          <RotateCcw className="w-3 h-3" strokeWidth={1.75} />
        </button>
      )}
      {redo && <RegenerateAnyway tools={redo} onAnswer={(yes) => (yes ? void regenerate(true) : setRedo(null))} />}
    </div>
  );
}

/** Before an answer that did things is written again: what it would do again, by tool. */
function RegenerateAnyway({ tools, onAnswer }: { tools: string[]; onAnswer: (yes: boolean) => void }) {
  return (
    <AlertDialog open onOpenChange={(open) => !open && onAnswer(false)}>
      <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">This answer did things</AlertDialogTitle>
          <AlertDialogDescription className="text-[13px] leading-[1.6]">
            {tools.length > 0 && <><span className="font-mono text-[12px] text-[var(--text-secondary)]">{tools.join(", ")}</span>. </>}
            Regenerating does them again.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction className={confirmDangerCls} onClick={() => onAnswer(true)}>Regenerate anyway</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ---------- block-list renderer for agent messages ---------- */
export function MessageBody({ blocks, text, onOpenArtifact, activeArtifactId }: {
  blocks?: MessageBlock[];
  text?: string;
  onOpenArtifact?: (a: Artifact) => void;
  activeArtifactId?: string;
}) {
  if (!blocks) {
    return <Markdown text={text || ""} />;
  }
  return (
    <div className="space-y-3 min-w-0">
      {blocks.map((b, i) => {
        switch (b.type) {
          case "text":
            return <Markdown key={i} text={b.text} />;
          case "code":
            return <CodeBlock key={i} code={b.code} lang={b.lang} title={b.filename} />;
          case "image":
            return <ImageBlock key={i} src={b.src} alt={b.alt} caption={b.caption} />;
          case "html":
            return <InlineHtml key={i} title={b.title} height={b.height} srcDoc={b.srcDoc} />;
          case "files":
            return <AttachmentRow key={i} files={b.files} align="start" />;
          case "chart":
            return <ChartBlock key={i} title={b.title} kind={b.kind} data={b.data} note={b.note} />;
          case "audio":
            return <AudioBlock key={i} duration={b.duration} label={b.label} />;
          case "artifact":
            return (
              <ArtifactCard
                key={i}
                artifact={b.artifact}
                onOpen={onOpenArtifact}
                active={activeArtifactId === b.artifact.id}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

/* The three text-ish keys the projection below probes. Each lives on only part
   of the `MessageBlock` union — `text` on a text block, `code` on a code block,
   `title` on html and chart — and the fallback chain reads all three off every
   member. Widening the callback's parameter is what lets it: every member of
   the union is assignable to this, so the expression stays exactly as written.
   `type` is carried so this is not a weak type — without one required key in
   common, TS rejects the blocks that happen to have none of the three. */
type TextishBlock = { type: string; text?: string; code?: string; title?: string };

// Plain-text projection of a message, for the copy action.
export function messageText(m: Message): string {
  if (m.text) return m.text;
  return (m.blocks || []).map((b: TextishBlock) => b.text || b.code || b.title || "").filter(Boolean).join("\n\n");
}
